import React from 'react';
import { KanbanIcon, HardHatIcon } from "@phosphor-icons/react";
import { Acquisition, AcquisitionPhase } from 'shared/types/acquisition';
import { AcquisitionKanbanView } from './AcquisitionKanbanView';

interface AcquisitionsSectionProps {
  acquisitions: Acquisition[];
  loading: boolean;
  onUpdate: () => void;
  onAcquisitionClick: (acquisition: Acquisition) => void;
  onAddClick?: (phase: AcquisitionPhase) => void;
  onCompleteAcquisition?: (acquisition: Acquisition) => void;
}

const AcquisitionsSectionComponent = ({
  acquisitions,
  loading,
  onUpdate,
  onAcquisitionClick,
  onAddClick,
  onCompleteAcquisition
}: AcquisitionsSectionProps) => {
  return (
    <div className="flex flex-col gap-3">
      {loading ? (
        <div className="flex flex-col items-center gap-4 py-12">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 border-4 border-moss-primary/20 border-t-moss-primary rounded-full animate-spin"></div>
            <HardHatIcon weight="duotone" size={28} className="text-moss-primary animate-bounce" />
          </div>
          <div className="text-center">
            <p className="text-lg font-medium text-neutral-primary">Acquisities aan het laden</p>
            <p className="text-sm text-neutral-600">Even geduld terwijl we je acquisities ophalen...</p>
          </div>
        </div>
      ) : acquisitions.length === 0 ? (
        <div className="text-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center">
              <KanbanIcon weight="duotone" size={32} className="text-neutral-400" />
            </div>
            <div>
              <p className="text-lg font-medium text-neutral-primary">Geen acquisities gevonden</p>
              <p className="text-sm text-neutral-600">Probeer andere filters of begin met het toevoegen van een nieuwe acquisitie</p>
            </div>
          </div>
        </div>
      ) : (
        <AcquisitionKanbanView
          acquisitions={acquisitions}
          onUpdate={onUpdate}
          onAcquisitionClick={onAcquisitionClick}
          onAddClick={onAddClick}
          onCompleteAcquisition={onCompleteAcquisition}
        />
      )}
    </div>
  );
};

export const AcquisitionsSection = React.memo(AcquisitionsSectionComponent, (prevProps, nextProps) => {
  // Return true if props are equal (should NOT re-render)
  // Return false if props are different (should re-render)
  
  // If loading state changed, re-render
  if (prevProps.loading !== nextProps.loading) {
    return false;
  }
  
  // If acquisitions array length changed, re-render
  if (prevProps.acquisitions.length !== nextProps.acquisitions.length) {
    return false;
  }
  
  // Check if any acquisition IDs changed (shallow comparison)
  const prevIds = prevProps.acquisitions.map(a => a.id).join(',');
  const nextIds = nextProps.acquisitions.map(a => a.id).join(',');
  if (prevIds !== nextIds) {
    return false;
  }
  
  // Ignore callback reference changes (onUpdate, onAcquisitionClick)
  // They're stable but might have new references on parent re-render
  
  // If all checks pass, don't re-render
  return true;
});

AcquisitionsSection.displayName = 'AcquisitionsSection';

